import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  OrganisationTagExternal,
  useOrganisationTagUpdateFollowedStatusMutation,
} from '../../services';

export const useTagFollow = () => {
  const { t } = useTranslation();

  const [updateFollowedStatus] = useOrganisationTagUpdateFollowedStatusMutation();

  const [followedById, setFollowedById] = useState<Record<number, boolean>>({});

  const [isFollowPending, setIsFollowPending] = useState(false);

  const isTagFollowed = useCallback(
    ({ id, feedFollowedStatus }: OrganisationTagExternal) => followedById[id] ?? feedFollowedStatus,
    [followedById]
  );

  const toggleTagFollow = useCallback(
    async (tag: OrganisationTagExternal) => {
      const { id: tagId } = tag;

      const status = !isTagFollowed(tag);

      setFollowedById((followedByIdPrev) => ({ ...followedByIdPrev, [tagId]: status }));
      setIsFollowPending(true);

      try {
        await updateFollowedStatus({ tagId, status }).unwrap();

        return true;
      } catch (_) {
        setFollowedById((followedByIdPrev) => ({ ...followedByIdPrev, [tagId]: !status }));
        toast.error(t('common.error-message'));

        return false;
      } finally {
        setIsFollowPending(false);
      }
    },
    [isTagFollowed, t, updateFollowedStatus]
  );

  return useMemo(
    () => ({ isTagFollowed, toggleTagFollow, isFollowPending }),
    [isFollowPending, isTagFollowed, toggleTagFollow]
  );
};
